// Question Session Report observability — lists report-generation runs (one row
// per submitted attempt the report ran on).
//
// Reads the DEDICATED `question-session-report-runs` container (OperationRun
// records), not `async-jobs`. The run record is the durable "this report was
// generated (or failed) for this session" answer, with token totals and the
// session correlation. See docs/FeatureDesigns/operation-run-records.md.
import { useMemo, useState } from 'react';
import { useOpenOperationRow } from './OperationNav';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { adminApi, type QuestionSessionReportRunView } from '../../api/adminApi';
import { DataTable, Badge, type ColumnDef } from '@shared-ui/components';

const STATUS_BADGE: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
  succeeded: 'success',
  failed: 'error',
  running: 'warning',
};

const formatLatency = (ms?: number) => (ms ? `${(ms / 1000).toFixed(1)}s` : '—');
const formatCost = (c?: number) => (typeof c === 'number' ? `$${c.toFixed(4)}` : '—');

export function QuestionSessionReportList() {
  const [status, setStatus] = useState('');
  const openRow = useOpenOperationRow();

  const { data, isLoading, error } = useQuery({
    queryKey: ['questionSessionReportRuns', status],
    queryFn: () => adminApi.getQuestionSessionReportRuns({ status: status || undefined, limit: 50 }),
  });

  const rows = data?.items ?? [];

  const columns = useMemo<ColumnDef<QuestionSessionReportRunView>[]>(() => [
    {
      key: 'started',
      header: 'Started',
      render: (r) => format(new Date(r.startedAt), 'MMM d, HH:mm:ss'),
    },
    {
      key: 'student',
      header: 'Student',
      render: (r) => (
        <div>
          <div>{r.studentName || '—'}</div>
          {r.studentEmail && (
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{r.studentEmail}</div>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (
        <Badge variant={STATUS_BADGE[r.status] ?? 'default'} size="sm">
          {r.status}
        </Badge>
      ),
    },
    {
      key: 'session',
      header: 'Session',
      render: (r) => (
        <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
          {r.correlation?.sessionId ? r.correlation.sessionId.slice(0, 8) : '—'}
        </span>
      ),
    },
    {
      key: 'model',
      header: 'Model / tokens',
      render: (r) =>
        r.llm ? (
          <div>
            <div>{r.llm.provider || '—'}</div>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
              {r.llm.inputTokens}→{r.llm.outputTokens} · {formatCost(r.llm.costUsd)}
            </div>
          </div>
        ) : (
          '—'
        ),
    },
    {
      key: 'latency',
      header: 'Latency',
      render: (r) => formatLatency(r.latencyMs),
    },
    {
      key: 'error',
      header: 'Error',
      render: (r) =>
        r.error ? (
          <span style={{ color: 'var(--color-error, #c2321f)', fontSize: 12 }}>{r.error}</span>
        ) : (
          '—'
        ),
    },
  ], []);

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <label style={{ fontSize: 13 }}>
          Status:{' '}
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All</option>
            <option value="succeeded">Succeeded</option>
            <option value="failed">Failed</option>
            <option value="running">Running</option>
          </select>
        </label>
      </div>

      {error ? (
        <div style={{ color: 'var(--color-error, #c2321f)' }}>Failed to load report runs.</div>
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          loading={isLoading}
          rowKey={(r) => r.id}
          onRowClick={(r) => openRow(rows, r, '/questionsessionreport')}
          emptyMessage="No report runs yet."
        />
      )}
    </div>
  );
}
